import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from 'zustand';
import type { ConfiguratorPreviewContext } from './preview-context';
import { fetchComponent, ComponentFetchError } from './api/fetchComponent';
import type { V3ComponentResponse } from './api/types';
import { parseComponent } from './parse/parseComponent';
import { Shell } from './ui/Shell';
import { CustomCodeInjector } from './ui/CustomCodeInjector';
import { Header } from './ui/Header';
import { BottomBar } from './ui/BottomBar';
import { useGroupNavigator } from './ui/GroupNavigator';
import { SummaryPanel } from './ui/SummaryPanel';
import { CombinedImage } from './ui/CombinedImage';
import { useExternalMount } from './ui/useExternalMount';
import { StoreProvider, useConfigentoStoreRef } from './state/StoreContext';
import { ComponentProvider, themeStyle } from './state/ComponentContext';
import { InstanceIdContext } from './state/InstanceContext';
import { WebshopProvider } from './state/WebshopContext';
import type { ConfigentoStore } from './state/store';
import { RuleResultsProvider } from './rules/RuleResultsContext';
import { resolvePredefineWrites } from './rules/predefine';
import { deriveConfiguration } from './state/derived';
import type { ExternalValues } from './eval/evaluator';
import { fetchWebserviceValues } from './eval/webservice';
import { EvalProvider } from './eval/EvalContext';
import { calculatePrice } from './pricing/calculate';
import { PriceProvider } from './pricing/PriceContext';
import { useTranslation, type Locale } from './i18n';
import { matchGateLocale } from './i18n/languages';
import { LanguageGate } from './ui/LanguageGate';
import { VERSION } from './version';
import { attachInstanceApi, publicApi, getInstanceEventListeners, getProductAttributeOverride, subscribeProductAttributes, subscribeLanguageSelect } from './public-api';
import { createInstanceApi } from './api/instance';
import { fireComponentViewed } from './api/componentView';
import type { Component } from './schema';
import { pickLocale } from './schema/localized';
import { productAttributeValues } from './eval/productAttributes';

interface ConfiguratorRootProps {
    preview?: ConfiguratorPreviewContext | undefined;
    /** Optional store override (used by tests). Production code lets the Provider create one. */
    store?: ConfigentoStore;
    /**
     * Public-API instance id this root belongs to. Used to attach the live
     * instance API once the Component finishes loading so plugin callers
     * can `window.Configento.instances.get(id).finalize()`. Optional —
     * left out when the root is rendered in a synthetic context (tests).
     */
    instanceId?: string;
    /**
     * Language the visitor chose at the gate, spelled the way the Component
     * spells it (`en_GB`, not the bundle's `en_US`). Undefined until they
     * pick — which is what keeps every existing embed on its old behaviour.
     */
    pickedLocale?: string;
    /** Told when they pick, so the host can swap the I18nProvider's locale. */
    onLocalePicked?: (code: string) => void;
    /** Told when the host asks for the gate again (`showLanguageSelect()`). */
    onLocaleReset?: () => void;
    /**
     * The Component request main.tsx started at boot (or the embedded
     * Component). Without it the root fetches on mount, as tests do.
     */
    componentRequest?: Promise<V3ComponentResponse> | undefined;
}

type FetchState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'loaded'; response: V3ComponentResponse }
    | { kind: 'error'; status: number | null; message: string };

export function ConfiguratorRoot({
    preview,
    store,
    instanceId,
    pickedLocale,
    onLocalePicked,
    onLocaleReset,
    componentRequest,
}: ConfiguratorRootProps = {}) {
    return (
        <StoreProvider store={store}>
            <ConfiguratorRootInner
                preview={preview}
                instanceId={instanceId}
                pickedLocale={pickedLocale}
                onLocalePicked={onLocalePicked}
                onLocaleReset={onLocaleReset}
                componentRequest={componentRequest}
            />
        </StoreProvider>
    );
}

function ConfiguratorRootInner({
    preview,
    instanceId,
    pickedLocale,
    onLocalePicked,
    onLocaleReset,
    componentRequest,
}: {
    preview?: ConfiguratorPreviewContext | undefined;
    instanceId?: string;
    pickedLocale?: string;
    onLocalePicked?: (code: string) => void;
    onLocaleReset?: () => void;
    componentRequest?: Promise<V3ComponentResponse> | undefined;
}) {
    const [state, setState] = useState<FetchState>(() =>
        preview ? { kind: 'loading' } : { kind: 'idle' },
    );

    // The boot-time request belongs to the first preview this root sees; a
    // later preview (tests swap it) fetches on its own.
    const bootRequest = useRef(componentRequest);
    useEffect(() => {
        if (!preview) return;
        let cancelled = false;
        const request = bootRequest.current ?? fetchComponent(preview);
        bootRequest.current = undefined;
        request
            .then((response) => {
                if (!cancelled) setState({ kind: 'loaded', response });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                if (err instanceof ComponentFetchError) {
                    setState({ kind: 'error', status: err.status, message: err.message });
                } else {
                    setState({
                        kind: 'error',
                        status: null,
                        message: err instanceof Error ? err.message : String(err),
                    });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [preview]);

    const component = useMemo<Component | null>(
        () => (state.kind === 'loaded' ? parseComponent(state.response) : null),
        [state],
    );
    // Content locale picks `title[locale]` / `info[locale]`. Priority:
    //   1. `preview.contentLocale` — admin preview toggle override, lets the
    //      merchant inspect en_GB/fr_FR/… variants without changing the
    //      stored component default.
    //   2. `preview.uiLocale` — merchant-set locale on the HTML5 embed
    //      (`data-configento-locale`). Most merchants set one locale and
    //      expect it to drive BOTH bundle chrome and component content;
    //      forcing them to duplicate the same value into a separate
    //      contentLocale attribute is a surprising foot-gun. Advanced
    //      merchants who want de content with en chrome can still set
    //      contentLocale explicitly and it wins per (1).
    //   3. `component.defaultLocale` (legacy: what's in the JSON).
    //   4. `component.setting.defaultLocale` (older JSON shape).
    //   0. `pickedLocale` — the visitor answered the language gate. Their
    //      answer outranks everything the merchant configured, because the
    //      merchant's locale is a default and this is a decision.
    const locale = component
        ? pickedLocale
            ?? preview?.contentLocale
            ?? preview?.uiLocale
            ?? component.defaultLocale
            ?? component.setting.defaultLocale
        : undefined;

    // Subscribe to selections so rules re-evaluate when the user changes a pick.
    const store = useConfigentoStoreRef();
    const selections = useStore(store, (s) => s.selections);

    // Webservice URLs are fetched once at mount via the cloud-side CORS
    // proxy. Results live in a Map keyed by Characteristic id; the
    // evaluator picks them up via the externalValues parameter so
    // downstream expressions/combi/matrix calculations can reference
    // a webservice-resolved value the same way they reference a user
    // selection. The empty initial value means the evaluator returns
    // null for these chars until the fetch resolves — fine because
    // the calculated chain is null-tolerant.
    // Product attributes come with the embed (shop plugins pass the values
    // of the product the configurator sits on) and are known at mount.
    // A host that switches variants in place can replace them later via
    // `instance.setProductAttributes()`; that value wins over the mount's.
    const attributeOverride = useSyncExternalStore(
        useCallback((cb: () => void) => (instanceId ? subscribeProductAttributes(instanceId, cb) : () => {}), [instanceId]),
        () => (instanceId ? getProductAttributeOverride(instanceId) : undefined),
        // Server render (tests, prerender): no host has set anything yet.
        () => undefined,
    );
    const productAttributes = attributeOverride ?? preview?.productAttributes;
    const [externalValues, setExternalValues] = useState<ExternalValues>(() => new Map());
    useEffect(() => {
        if (!component) return;
        const fromProduct = productAttributeValues(component, productAttributes);
        setExternalValues(fromProduct);
        let cancelled = false;
        fetchWebserviceValues(component, preview?.cloudBaseUrl)
            .then((map) => {
                if (cancelled) return;
                if (map.size > 0) setExternalValues(new Map([...fromProduct, ...map]));
            })
            .catch((err) => {
                console.warn('[Configento] webservice fetch pass failed:', err);
            });
        return () => { cancelled = true; };
    }, [component, preview?.cloudBaseUrl, productAttributes]);

    // One pass for the whole render: rules, the selections those rules leave
    // standing, and the calculated values derived from them. Consumers that
    // compute (price, payload, combined image) read `derived.selections`;
    // the individual Characteristic inputs keep rendering the store's raw
    // pick so it reappears when a rule stops matching.
    const derived = useMemo(
        () =>
            deriveConfiguration({
                component: component ?? EMPTY_COMPONENT,
                selections,
                externalValues,
            }),
        [component, selections, externalValues],
    );
    const { rules: ruleResult, evaluated } = derived;
    // Host-shop tax rate flows through the preview iframe contract
    // (`window._configentoPreview.taxRate`). When embedded via a plugin in
    // Phase 4, the same field carries the live shop rate; for standalone
    // preview it's whatever the admin embedded the iframe with. Parsed once
    // here so calculatePrice gets a clean number-or-undefined.
    const storeTaxRate = useMemo(() => parseHostTaxRate(preview?.taxRate), [preview?.taxRate]);
    const priceBreakdown = useMemo(
        () =>
            calculatePrice(component ?? EMPTY_COMPONENT, derived.selections, evaluated, {
                storeTaxRate,
                displayCurrency: preview?.currency,
                currencyRates: preview?.currencyRates,
                // Hidden characteristics (rule-driven blacklist) shouldn't
                // count toward the total or appear in the summary, even when
                // a value is still picked from before the rule kicked in.
                hiddenCharacteristics: ruleResult.hiddenCharacteristics,
                // Stale predefine pointing at a now-blacklisted value would
                // otherwise stay in the total + summary even though the UI
                // already dropped the option.
                blacklistedValues: ruleResult.blacklistedValues,
            }),
        [
            component,
            derived.selections,
            evaluated,
            storeTaxRate,
            preview?.currency,
            preview?.currencyRates,
            ruleResult.hiddenCharacteristics,
            ruleResult.blacklistedValues,
        ],
    );

    // Apply rule-driven predefines. `resolvePredefineWrites` owns the mode
    // semantics, so what comes back is already "these Characteristics get
    // these Values" — hence `force`, which is the difference between a rule
    // and the default seeding below.
    //
    // Reads the store's raw `selections`, not `derived.selections`: mode '3'
    // fires precisely *because* the current pick was blacklisted, which the
    // effective map has already stripped.
    //
    // A write re-renders, which re-evaluates the rules; the second pass then
    // resolves to the same Value and `applyPredefine` bails on identity, so
    // this settles after at most one store-write per Characteristic.
    useEffect(() => {
        if (!component) return;
        const { applyPredefine } = store.getState();
        const writes = resolvePredefineWrites({
            component,
            predefinedSelections: ruleResult.predefinedSelections,
            selections,
            rules: ruleResult,
        });
        for (const { characteristicId, valueId } of writes) {
            applyPredefine(characteristicId, valueId, { force: true });
        }
    }, [component, ruleResult, selections, store]);

    // Seed schema-level defaults (legacy `default_value`).
    // Runs once per Component load; applyPredefine itself respects userTouched
    // and is idempotent, so re-running on later renders is harmless.
    //
    // Free-entry defaults are seeded HERE and not only in TextView, which also
    // does it on mount. The difference matters as soon as a Characteristic is
    // not rendered: in a wizard only the current step exists in the DOM, so a
    // number field two steps along never mounted, never seeded, and every
    // formula and matrix reading it got nothing. The configurator then opened
    // on a price that was too low — or zero — and silently corrected itself
    // once the visitor walked far enough. Selection defaults never had the
    // problem because they were always seeded from here.
    useEffect(() => {
        if (!component) return;
        const { applyPredefine, setText, selections } = store.getState();
        for (const group of component.groups) {
            for (const c of group.characteristics) {
                if ('defaultValueId' in c && c.defaultValueId !== undefined) {
                    applyPredefine(c.id, c.defaultValueId);
                    continue;
                }
                if (c.type !== 'text' && c.type !== 'area') continue;
                // `useDefaultAsPlaceholder` means the default is a hint, not a
                // value — the field is meant to start empty, so seeding it
                // would put text in a box the merchant wanted blank.
                if (c.useDefaultAsPlaceholder) continue;
                // Never overwrite what is already there: a restored snapshot
                // or a deep link has already said what this field holds.
                if (selections[c.id] !== undefined) continue;
                const text = pickLocale(c.defaultValue, locale);
                if (text) setText(c.id, text);
            }
        }
    }, [component, locale, store]);

    // Hot-swap the public-API methods on the registered instance once the
    // Component is parsed. Plugin authors can hold a reference to
    // `Configento.instances.get(id)` BEFORE this fires (the methods start
    // as throwing stubs) and the same object will gain live methods here
    // without the plugin re-querying the map.
    const { locale: uiLocale } = useTranslation();
    useEffect(() => {
        if (!component || !instanceId) return;
        // The WebshopConfig was attached at registerInstance time in main.tsx.
        // We need it here so `storeAsset()` can resolve cloud auth — pulling
        // it back from the registry avoids threading another prop through
        // every callsite (ConfiguratorRoot is rendered from main.tsx, tests,
        // and the admin preview shell).
        const webshopConfig = publicApi.instances.get(instanceId)?.config ?? null;
        // Persistent listeners live on the MutableInstance so subscribers
        // registered ONCE (compat's inst.on('price', …) at Configento.ready)
        // survive every subsequent re-attach — attach re-runs on every
        // externalValues change (webservice values finishing after the
        // initial component fetch), and without shared listeners the
        // callback registered against the previous closure would be
        // orphaned and never fire again from the fresh closure.
        const eventListeners = getInstanceEventListeners(instanceId);
        const { dispose, ...api } = createInstanceApi({
            component,
            store,
            locale,
            uiLocale,
            externalValues,
            webshopConfig,
            eventListeners,
            storeTaxRate,
        });
        attachInstanceApi(instanceId, api, dispose);
    }, [component, instanceId, store, locale, uiLocale, externalValues, storeTaxRate]);

    // The reserved height (`minHeight`) only bridges the load. Once the first
    // step is on screen the configurator sizes itself again, unless the host
    // asked to keep it.
    useEffect(() => {
        if (!component || !instanceId) return;
        const instance = publicApi.instances.get(instanceId);
        if (instance?.config?.minHeight && !instance.config.minHeightKeep) {
            instance.element.style.minHeight = '';
        }
    }, [component, instanceId]);

    // Fire `component.viewed` telemetry exactly ONCE per bundle mount, once
    // the Component has parsed and we're in a real storefront embed. Editor
    // previews are explicitly skipped — the spec's rationale is that
    // preview loads aren't real end-user engagement. The admin-side registry
    // throttle (10min per component+session) additionally suppresses reload
    // spam if the same tab hits the same component multiple times.
    //
    // useRef guard: this effect depends on `component` only, but React 18's
    // dev-mode double-invoke of effects would fire twice per mount without
    // the ref. Production build calls it once even without the guard, but
    // keeping it makes local `npm run dev` traces match production.
    const componentViewFiredRef = useRef(false);
    useEffect(() => {
        if (!component || !instanceId) return;
        if (componentViewFiredRef.current) return;
        if (preview?.embedMode !== 'storefront') return;

        const webshopConfig = publicApi.instances.get(instanceId)?.config ?? null;
        if (!webshopConfig) return;
        componentViewFiredRef.current = true;
        // Fire and forget — helper handles all error paths internally.
        void fireComponentViewed(webshopConfig);
    }, [component, instanceId, preview?.embedMode]);

    const { nav, main } = useGroupNavigator(
        component ?? EMPTY_COMPONENT,
        locale,
        {
            instanceId,
            wizardNav: instanceId ? publicApi.instances.get(instanceId)?.config?.wizardNav : undefined,
            ruleResults: ruleResult,
        },
    );

    // Chrome placement: resolved here because instanceId is in scope, and
    // memoised on the two things that can change it — the instance registers
    // its frozen config once, and preview arrives with the mount.
    const placement = useMemo(
        () => resolvePlacement(instanceId, preview),
        [instanceId, preview],
    );

    // ── Language gate ─────────────────────────────────────────────────────
    const gateLanguages = useMemo(
        () => (preview?.languageSelect ? resolveGateLanguages(component, preview.languages) : []),
        [component, preview?.languageSelect, preview?.languages],
    );

    // Per Component, not per page: two configurators on one page may well
    // carry different language sets, and answering for one should not answer
    // for the other.
    const storageKey = preview?.componentId
        ? `configento:locale:${preview.componentId}`
        : null;

    // Restore the choice from earlier in this browsing session. sessionStorage
    // and not localStorage on purpose: coming back to the product page should
    // not ask again, a visit next week should.
    useEffect(() => {
        if (!preview?.languageSelect || pickedLocale || gateLanguages.length < 2) return;
        if (!storageKey || !onLocalePicked) return;
        // Only honour a stored code that is still on offer: the Component may
        // have lost a language since, and a code nothing matches would send
        // every title through pickLocale's "first non-empty" fallback.
        const stored = readSessionLocale(storageKey);
        if (stored && gateLanguages.some((lang) => lang.code === stored)) onLocalePicked(stored);
    }, [preview?.languageSelect, pickedLocale, gateLanguages, storageKey, onLocalePicked]);

    // `instance.showLanguageSelect()`: forget the answer, here and in the
    // session, so the gate comes back. The store sits above the gate, so the
    // selections survive the round trip.
    useEffect(() => {
        if (!instanceId || !onLocaleReset) return;
        return subscribeLanguageSelect(instanceId, () => {
            removeSessionLocale(storageKey);
            onLocaleReset();
        });
    }, [instanceId, storageKey, onLocaleReset]);

    // One language is not a choice, and a Component the merchant never
    // translated should not grow a click in front of it.
    const showGate =
        Boolean(preview?.languageSelect) && !pickedLocale && gateLanguages.length >= 2;

    if (state.kind === 'idle' || !component) {
        // Pass embedMode here too so the storefront-mode CSS resets
        // (font: inherit, no card chrome, etc.) apply to the loading
        // / error / idle states. Without this the LoadingCard renders
        // in the bundle's default mini-Schrift on top of the host
        // theme — visible as "viel zu kleine Lademeldung" on the
        // carport storefront before the Component finishes loading.
        return (
            <Shell embedMode={placement.isStorefront ? 'storefront' : preview?.embedMode}>
                {state.kind === 'loading' && <LoadingCard />}
                {state.kind === 'error' && (
                    <ErrorCard status={state.status} message={state.message} />
                )}
                {state.kind === 'idle' && <IdlePlaceholder />}
                {preview && <PreviewDebugBlock preview={preview} />}
            </Shell>
        );
    }

    if (showGate) {
        return (
            <Shell embedMode={placement.isStorefront ? 'storefront' : preview?.embedMode}>
                <LanguageGate
                    languages={gateLanguages}
                    title={preview?.languageSelectTitle}
                    onPick={(code) => {
                        writeSessionLocale(storageKey, code);
                        onLocalePicked?.(code);
                    }}
                />
            </Shell>
        );
    }

    return (
        <ComponentProvider component={component}>
            <InstanceIdContext.Provider value={instanceId}>
                <WebshopProvider preview={preview}>
                    <RuleResultsProvider value={ruleResult}>
                        <EvalProvider value={evaluated}>
                            <PriceProvider value={priceBreakdown}>
                                <LoadedShell
                                    component={component}
                                    locale={locale}
                                    nav={nav}
                                    main={main}
                                    preview={preview}
                                    placement={placement}
                                />
                            </PriceProvider>
                        </EvalProvider>
                    </RuleResultsProvider>
                </WebshopProvider>
            </InstanceIdContext.Provider>
        </ComponentProvider>
    );
}

function LoadedShell({
    component,
    locale,
    nav,
    main,
    preview,
    placement,
}: {
    component: Component;
    locale: string | undefined;
    nav: ReturnType<typeof useGroupNavigator>['nav'];
    main: ReturnType<typeof useGroupNavigator>['main'];
    preview: ConfiguratorPreviewContext | undefined;
    placement: ChromePlacement;
}) {
    const { t } = useTranslation();

    // Admin-configured design tokens push down as CSS custom properties on
    // the cfg-root scope. Today: only the active/accent colour the admin
    // picks in Settings → Design. The CSS already references --cfg-color-
    // accent for radio/checkbox/listimage selected states, the nav-tab
    // underline, ActionButton bg, etc — so a single var override re-skins
    // the whole instance. Falsy / unset → fall through to the default.
    const rootStyle = themeStyle(component);

    // Where the chrome goes. Three independent decisions, all resolved off
    // the instance's WebshopConfig so a data attribute can drive them as well
    // as the preview context.
    //
    // `storefront` suppresses the total and the summary outright: the host
    // shop has its own price box and its own summary (Magento places it
    // left/right/tab per its `product_summary` setting, see configento.xml),
    // and ours would double them up.
    //
    // A mount selector is the other half, and the one legacy had: point it at
    // an element on the page and that widget renders there instead of inside
    // the configurator. `html5.js:setPrice()` filled `#configento-price`
    // whenever it existed; `_cssPathCombinedImage` did the same for the
    // image. Selectors instead of fixed ids, so two configurators on one page
    // don't fight over the same target.
    //
    // Note the combined image no longer requires storefront mode. It used to,
    // which meant a CMS snippet could not move the image out of the
    // configurator without also losing the total and the summary — a coupling
    // with no reason behind it.
    const {
        isStorefront, combinedImageMount, priceMount, summaryMount, summaryOpen,
        summaryOpenRequested,
    } = placement;

    // `summary-open` is the initial state of a panel — it cannot conjure one.
    // A storefront embed renders no summary of its own (that chrome belongs to
    // the merchant's theme), so without `summary-mount` there is nothing to
    // open and the attribute sits there doing nothing. Say so rather than
    // leaving the host to diff the bundle looking for the attribute name.
    const summaryOpenIsInert = summaryOpenRequested && isStorefront && !summaryMount;
    useEffect(() => {
        if (!summaryOpenIsInert) return;
        console.warn(
            '[Configento] data-configento-summary-open has no effect without '
            + 'data-configento-summary-mount: a storefront embed renders no summary panel '
            + 'of its own, so there is nothing to open. Point summary-mount at an element '
            + 'on the page to get one.',
        );
    }, [summaryOpenIsInert]);

    return (
        <Shell
            style={rootStyle}
            embedMode={isStorefront ? 'storefront' : preview?.embedMode}
            // Storefront mode: omit our own Header entirely. The shop's
            // product page already provides the page-level title; the
            // configurator just renders the form. (Plus the Header
            // previously emitted an <h1>, which would have dueled with
            // the product page's H1 for SEO.)
            header={
                isStorefront ? null : (
                    <Header
                        title={component.title}
                        headline={component.headline}
                        locale={locale}
                        fallbackTitle={t('fallback.component_title', { id: component.id })}
                    />
                )
            }
            nav={nav}
            bottomBar={isStorefront || priceMount ? null : <BottomBar />}
        >
            <CombinedImage
                component={component}
                locale={locale}
                mountSelector={combinedImageMount}
            />
            <CustomCodeInjector
                css={component.customCss}
                javascript={component.customJavascript}
            />
            {main}
            {!isStorefront && !summaryMount && <SummaryPanel locale={locale} defaultOpen={summaryOpen} />}
            {priceMount   && <ExternalWidget selector={priceMount}><BottomBar /></ExternalWidget>}
            {summaryMount && <ExternalWidget selector={summaryMount}><SummaryPanel locale={locale} defaultOpen={summaryOpen} /></ExternalWidget>}
            {preview && <PreviewDebugBlock preview={preview} />}
        </Shell>
    );
}

// Re-exported: tests and older callers import it from here.
export { productAttributeValues };

/**
 * Parse the host-shop tax rate the embedding plugin hands us through the
 * preview context. Tolerant of the legacy wire format which encodes it as a
 * string. Empty / non-numeric / explicit "null" → undefined so calculatePrice
 * stays in identity mode.
 */
function parseHostTaxRate(raw: string | undefined): number | undefined {
    if (raw == null || raw === '' || raw === 'null' || raw === 'undefined') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
}

/**
 * One tile on the language gate.
 *
 * The two codes are deliberately separate. Real Components in the field spell
 * English `en_GB`, while the bundle ships its own strings under `en_US` — and
 * `pickLocale` has no language-level fallback, so handing `en_US` to a
 * Component whose titles live under `en_GB` would quietly serve the first
 * non-empty entry instead. In practice: the visitor picks English and gets
 * German. Hence `code` goes to the Component and `locale` only picks the
 * label and the flag.
 */
export interface GateLanguage {
    /** Locale code as the Component spells it — what the content lookup uses. */
    code: string;
    /** The supported locale that gives the tile its name and flag. */
    locale: Locale;
}

/**
 * Which languages the gate offers.
 *
 * An explicit list from the embed wins — that is a merchant saying "this shop
 * sells in these two of the four languages the Component knows". Otherwise the
 * Component decides, which is the better default because the list then cannot
 * drift away from what was actually translated.
 *
 * Codes the bundle can neither name nor draw drop out rather than fall back to
 * English: an unlabelled tile is worse than one language fewer.
 */
export function resolveGateLanguages(
    component: Component | null,
    explicit: ReadonlyArray<string> | undefined,
): ReadonlyArray<GateLanguage> {
    const componentCodes = component ? componentLocales(component) : [];
    const wanted = explicit && explicit.length > 0 ? explicit : componentCodes;

    const seen = new Set<Locale>();
    const out: GateLanguage[] = [];
    for (const raw of wanted) {
        const locale = matchGateLocale(raw);
        // Two tiles both reading "English" would be a coin toss, so the first
        // spelling of a language wins.
        if (!locale || seen.has(locale)) continue;
        seen.add(locale);
        out.push({ code: alignToComponent(raw, componentCodes), locale });
    }
    return out;
}

/**
 * Translate a merchant's spelling into the Component's own.
 *
 * `data-configento-languages="de,en"` against a Component carrying `de_DE` and
 * `en_GB` has to end up at those two codes, not at the shorthand — the content
 * lookup is a plain key match. An exact hit wins; otherwise the first
 * Component code for the same language does; failing both, the merchant's own
 * string stands (a Component that declares a language it has no titles for
 * cannot be rescued here anyway).
 */
function alignToComponent(raw: string, componentCodes: ReadonlyArray<string>): string {
    if (componentCodes.includes(raw)) return raw;
    const lang = raw.replace(/[-.]/g, '_').toLowerCase().split('_')[0];
    const sameLanguage = componentCodes.find(
        (code) => code.replace(/[-.]/g, '_').toLowerCase().split('_')[0] === lang,
    );
    return sameLanguage ?? raw;
}

/**
 * The languages a Component carries.
 *
 * `setting.selectedLanguages` is what the admin's language picker writes and
 * therefore the merchant's own statement of intent. Older Components predate
 * that field, so when it is empty we read the locale keys off the title maps
 * instead — the Component title plus the group titles are enough of a sample
 * and cost nothing compared to walking every Value.
 */
function componentLocales(component: Component): ReadonlyArray<string> {
    const declared = component.setting.selectedLanguages;
    if (declared.length > 0) return declared;

    const keys = new Set<string>(Object.keys(component.title ?? {}));
    for (const group of component.groups) {
        for (const key of Object.keys(group.title ?? {})) keys.add(key);
    }
    return [...keys];
}

/**
 * sessionStorage, but never a reason to fail.
 *
 * Reading it throws outright in a browser with site data blocked, and the
 * language gate is the first thing a visitor sees — a configurator that dies
 * there dies before it showed anything at all. A lost preference just means
 * asking once more.
 */
function readSessionLocale(key: string): string | null {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function removeSessionLocale(key: string | null): void {
    if (!key) return;
    try {
        window.sessionStorage.removeItem(key);
    } catch {
        // Nothing stored that could bring the old answer back.
    }
}

function writeSessionLocale(key: string | null, code: string): void {
    if (!key) return;
    try {
        window.sessionStorage.setItem(key, code);
    } catch {
        // Private mode, blocked site data: the choice still applies to this
        // render, it just will not survive the next page load.
    }
}

function IdlePlaceholder() {
    const { t } = useTranslation();
    return (
        <div className="cfg-state-card cfg-state-card--loading" data-configento-state="idle">
            <div className="cfg-state-card__title">configento {VERSION}</div>
            <div className="cfg-state-card__detail">{t('state.idle.detail')}</div>
        </div>
    );
}

function LoadingCard() {
    const { t } = useTranslation();
    return (
        <div
            className="cfg-skeleton"
            data-configento-state="loading"
            role="status"
            aria-busy="true"
        >
            <span className="cfg-skeleton__label">{t('state.loading')}</span>
            <div className="cfg-skeleton__bar cfg-skeleton__bar--title" />
            <div className="cfg-skeleton__bar" />
            <div className="cfg-skeleton__bar" />
            <div className="cfg-skeleton__bar cfg-skeleton__bar--short" />
        </div>
    );
}

function ErrorCard({ status, message }: { status: number | null; message: string }) {
    const { t } = useTranslation();
    return (
        <div
            className="cfg-state-card cfg-state-card--error"
            data-configento-state="error"
        >
            <div className="cfg-state-card__title">
                {status !== null
                    ? t('state.error.title_with_status', { status })
                    : t('state.error.title')}
            </div>
            <div className="cfg-state-card__detail">{message}</div>
            {status === 401 && (
                <div className="cfg-state-card__detail">{t('state.error.token_expired')}</div>
            )}
        </div>
    );
}

/**
 * Drops the preview context into the iframe's devtools instead of painting
 * a yellow box at the bottom of the layout. Keeps the diagnostic available
 * (token prefix, cloud host, tax rate) for support without intruding on
 * the actual preview surface. Logs once per mount/preview reference.
 */
function PreviewDebugBlock({ preview }: { preview: ConfiguratorPreviewContext }) {
    useEffect(() => {
        console.info('[configento %s] preview context', VERSION, {
            componentId: preview.componentId,
            customer: preview.customer,
            cloudBaseUrl: preview.cloudBaseUrl,
            token: preview.token ? `${preview.token.slice(0, 8)}…` : '',
            taxRate: preview.taxRate,
            frontendVersion: preview.frontendVersion,
            uiLocale: preview.uiLocale,
        });
    }, [preview]);
    return null;
}

/**
 * Stable empty Component used to keep `useGroupNavigator`'s hook order
 * consistent while we're in idle / loading / error states. The hook NEEDS
 * to be called every render (Rules of Hooks); the empty value short-
 * circuits its internal branches.
 */
const EMPTY_COMPONENT: Component = {
    id: 0,
    title: {},
    headline: {},
    defaultLocale: undefined,
    frontendVersion: undefined,
    edition: 'PROFESSIONAL',
    requiresBundle: 'legacy',
    cmsBlockRefs: [],
    layout: 'no_groups',
    groupWizard: false,
    groupWizardAutoscroll: false,
    groupScrollOffset: 0,
    valueImagePosition: 'left',
    combinedImage: 'no',
    displayPrice: false,
    displayPriceType: 'absolute',
    displayPriceIfZero: false,
    displayPricePattern: undefined,
    priceIncludesTax: false,
    showMinMax: false,
    minMaxPattern: undefined,
    showCombinedSku: false,
    country: undefined,
    massFactor: undefined,
    altCheckout: false,
    svgExport: false,
    customCss: undefined,
    customJavascript: undefined,
    activeColor: undefined,
    brandColor: undefined,
    loadingAnimationColor: undefined,
    groups: [],
    rules: [],
    ruleGroups: [],
    setting: {
        taxRate: undefined,
        selectedLanguages: [],
        defaultLocale: undefined,
        defaultCurrencyCode: undefined,
        system: undefined,
        matrixDelimiter: undefined,
        priceIncludesTax: undefined,
        dataProtection: undefined,
    },
};

/**
 * Render `children` into a host-page element instead of here.
 *
 * Renders nothing when the selector matches nothing. That is the deliberate
 * choice: a host that named a target meant the widget to appear there, and
 * quietly falling back into the configurator would put a second price box
 * next to the shop's own — the exact duplication the selector exists to
 * avoid. A theme typo costs the widget, and the empty target says where to
 * look, which beats a widget in the wrong place.
 */
function ExternalWidget({ selector, children }: { selector: string; children: ReactNode }) {
    const el = useExternalMount(selector);
    if (!el) return null;
    return createPortal(children, el);
}

/** Where each piece of chrome renders — see the comment in LoadedShell. */
interface ChromePlacement {
    isStorefront: boolean;
    combinedImageMount: string | undefined;
    priceMount: string | undefined;
    summaryMount: string | undefined;
    summaryOpen: boolean;
    /**
     * Whether the host set `summary-open` itself, as opposed to inheriting the
     * default. Only needed to tell them when the attribute cannot do anything
     * — see the warning in {@link LoadedShell}.
     */
    summaryOpenRequested: boolean;
}

/**
 * Resolve chrome placement from the instance's WebshopConfig, falling back to
 * the preview context for the two fields that used to live only there.
 */
function resolvePlacement(
    instanceId: string | undefined,
    preview: ConfiguratorPreviewContext | undefined,
): ChromePlacement {
    const config = instanceId ? publicApi.instances.get(instanceId)?.config : undefined;

    return {
        isStorefront:       (config?.embedMode ?? preview?.embedMode) === 'storefront',
        combinedImageMount: config?.combinedImageMount ?? preview?.combinedImageMount,
        priceMount:         config?.priceMount,
        summaryMount:       config?.summaryMount,
        // A summary that got its own slot on the page is there to be read, so
        // it defaults to open there; inside the configurator it stays closed.
        summaryOpen:        config?.summaryOpen ?? Boolean(config?.summaryMount),
        summaryOpenRequested: config?.summaryOpen !== undefined,
    };
}
